import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig } from '../../../services/baseApplication/types';
import { PAGE_APP_CONFIG } from './PageConfig';
import { entityService } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';
import { pageCRDTService } from './crdt/pageCRDTService';

/**
 * Apply a base64-encoded Y.Doc state, handling both V1 (BaseApplication format,
 * Y.encodeStateAsUpdate) and V2 (localFirstSyncService format, Y.encodeStateAsUpdateV2).
 * V2 throws when decoded with Y.applyUpdate, so we try V2 first.
 */
function applyBase64State(ydoc: Y.Doc, base64: string): void {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  // Try V2 first (localFirstSyncService saves in V2 format)
  try {
    Y.applyUpdateV2(ydoc, bytes);
    return;
  } catch {
    // Fall through to V1
  }
  // Fall back to V1 (BaseApplication.saveToBackend saves in V1 format)
  Y.applyUpdate(ydoc, bytes);
}

const log = createFileLogger('PAGE_APPLICATION');

export class PageApplication extends BaseApplication {
  private entityName: string = '';

  constructor() {
    super();
    this.suggestionMode = false;
  }

  protected getConfig(): AppConfig {
    return PAGE_APP_CONFIG;
  }

  protected getWebSocketRoomPrefix(): string {
    return 'page';
  }

  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    log.info('[PageApplication] Loading page:', entityId);

    // Set entityId directly (skip BaseApplication.initialize() empty-doc path)
    this.entityId = entityId;

    // 1. Get synced Y.Doc from local-first service (IndexedDB loads instantly)
    // This is the PRIMARY data source per local-first architecture
    const { ydoc, websocket } = await pageCRDTService.getYDocForPage(entityId);
    this.ydoc = ydoc;
    this.collaborationProvider = websocket;
    this.yxml = this.ydoc.getXmlFragment('prosemirror');

    log.info('[PageApplication] Y.Doc loaded from local-first sync:', {
      yxmlLength: this.yxml.length,
      ydocGuid: this.ydoc.guid,
    });

    // 2. ONLY if IndexedDB is empty, fetch entity from backend as fallback
    if (this.yxml.length === 0) {
      log.warn('[PageApplication] Y.Doc empty after local-first sync, checking backend fallback');
      const entity = await entityService.get(entityId);
      if (!entity) throw new Error(`Page not found: ${entityId}`);

      this.entityName = entity.name || 'Untitled Page';

      if (entity.skeleton?.base64) {
        // Fall back to entity.skeleton.base64 (covers first open on new device)
        log.info('[PageApplication] Applying base64 state from backend');
        try {
          applyBase64State(this.ydoc!, entity.skeleton.base64);
          log.info('[PageApplication] Loaded base64 state, yxml length:', this.yxml.length);
        } catch (error) {
          log.error('[PageApplication] Failed to load base64 state, starting empty:', error);
          this.initializeEmptyPage();
        }
      } else if (entity.skeleton?.templateOps?.length) {
        // Template seeding: apply ops into an empty Y.Doc instead of blank page
        const templateOps = entity.skeleton.templateOps as any[];
        log.info('[PageApplication] Applying', templateOps.length, 'templateOps from skeleton');
        for (const op of templateOps) {
          try {
            await this.handleEntityWrite({
              operation: op.op || op.operation || 'insert',
              target: op.target,
              content: op.content,
              attrs: op.attrs,
              operations: op.operations,
            });
          } catch (opErr) {
            log.warn('[PageApplication] templateOp failed (non-fatal):', opErr);
          }
        }
        log.info('[PageApplication] templateOps applied, yxml length:', this.yxml.length);
        entityService.update(entityId, {
          skeleton: { ...entity.skeleton, templateOps: undefined },
        }).catch((err) => {
          log.warn('[PageApplication] Failed to clear templateOps from skeleton (non-fatal):', err);
        });
      } else {
        // Genuinely new page — initialize with an empty paragraph
        log.info('[PageApplication] No base64 state, initializing empty page');
        this.initializeEmptyPage();
      }
    } else {
      // Fast path: IndexedDB had content, fetch entity name only (without content)
      const entity = await entityService.get(entityId, { excludeContent: true });
      this.entityName = entity?.name || 'Untitled Page';
    }

    // 3. Build indices ONCE on correct Y.Doc
    await this.buildCoreIndices();
    log.info('[PageApplication] Indices built:', this.byIdIndex.size, 'nodes');

    if (this.byIdIndex.size === 0 && this.yxml.length > 0) {
      await this.assignMissingIds();
      await this.buildCoreIndices();
    }

    // 4. Set ready state
    this.loadingProgress = { minimal: true, partial: true, full: true };
    this.emit('ready');
    log.info('[PageApplication] Load complete');
  }

  async destroy(): Promise<void> {
    // Clean up sync providers before BaseApplication tears down the Y.Doc
    if (this.entityId) {
      pageCRDTService.cleanup(this.entityId);
    }
    await super.destroy();
  }

  private initializeEmptyPage(): void {
    if (!this.yxml) return;

    const paragraph = new Y.XmlElement('paragraph');
    paragraph.setAttribute('id', this.generateId());
    paragraph.setAttribute('className', '');
    paragraph.insert(0, [new Y.XmlText()]);

    this.yxml.insert(0, [paragraph]);
  }

  private async assignMissingIds(): Promise<void> {
    if (!this.yxml || !this.ydoc) return;

    let count = 0;

    this.ydoc.transact(() => {
      for (let i = 0; i < this.yxml!.length; i++) {
        const child = this.yxml!.get(i);
        if (child instanceof Y.XmlElement) {
          const id = child.getAttribute('id');
          if (!id || id === 'undefined' || id === 'null') {
            child.setAttribute('id', this.generateId());
            count++;
          }
        }
      }
    }, 'page-migration');

    log.info('[PageApplication] Assigned', count, 'missing IDs');
  }

  public getEntityName(): string {
    return this.entityName;
  }

  public _setEntityName(newName: string): void {
    this.entityName = newName;
  }

  public getIndexCount(): number {
    return this.indexManager?.size() ?? 0;
  }

  public async updateEntityName(newName: string): Promise<void> {
    const finalName = newName.trim() || 'Untitled Page';
    this.entityName = finalName;

    if (!this.entityId) return;

    const { default: useEntityStore } = await import('../../../stores/entityStore');
    await useEntityStore.getState().actions.updateEntity(this.entityId, { name: finalName });
  }
}
